import {
  AppBar,
  Box,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import logoRes from '../../../Assets/logo/logo.svg';
import { Link } from 'react-router-dom';
import { IoArrowBack, IoClose } from 'react-icons/io5';

const steps = ['Booking', 'Details', 'Confirm'];

const WidgetHeader = ({
  activeStep = 0,
  backHref,
  onBack,
  onClose,
  showClose = false,
  logo,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <AppBar
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div className="relative h-auto md:h-[80px] flex-row mx-auto w-[90%] lg:w-[80%] flex gap-x-3 items-center md:justify-between justify-between mt-2">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: { md: 140 },
          }}
        >
          {(onBack || backHref) && (
            <IconButton
              {...(onBack
                ? { onClick: onBack }
                : { component: Link, to: backHref })}
              size="small"
              sx={{ color: 'text.secondary' }}
              aria-label="Go back"
            >
              <IoArrowBack size={22} />
            </IconButton>
          )}
          <Link to="">
            <img src={logo || logoRes} alt="logo" width={50} />
          </Link>
        </Box>
        {!isMobile && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                width: '100%',
                ml: { xs: 0, sm: 4 },
                gap: 2,
              }}
            >
              {steps.map((step, index) => {
                const isActive = activeStep === index;
                const isCompleted = activeStep > index;

                return (
                  <Box
                    key={step}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: { xs: 44, sm: 25 },
                          height: { xs: 44, sm: 25 },
                          borderRadius: '50%',
                          bgcolor:
                            isActive || isCompleted
                              ? 'primary.main'
                              : 'action.disabledBackground',
                          color:
                            isActive || isCompleted
                              ? 'primary.contrastText'
                              : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          animation: isActive
                            ? 'stepBounce 1.8s ease-in-out infinite'
                            : 'none',
                          '@keyframes stepBounce': {
                            '0%, 100%': {
                              transform: 'translateY(0) scale(1)',
                            },
                            '50%': {
                              transform: 'translateY(-3px) scale(1.08)',
                            },
                          },
                          '&::after': isActive
                            ? {
                                content: '""',
                                position: 'absolute',
                                inset: -6,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: 'primary.main',
                                opacity: 0,
                                animation: 'stepPulse 1.8s ease-out infinite',
                              }
                            : {},
                          '@keyframes stepPulse': {
                            '0%': {
                              transform: 'scale(0.75)',
                              opacity: 0.5,
                            },
                            '70%, 100%': {
                              transform: 'scale(1.35)',
                              opacity: 0,
                            },
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', sm: '12px' },
                            fontWeight: 500,
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontSize: { xs: '12px', sm: '12px' },
                          fontWeight: 500,
                          color: 'text.secondary',
                        }}
                      >
                        {step}
                      </Typography>
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          width: { xs: 36, sm: 50 },
                          height: 3,
                          bgcolor: isCompleted ? 'primary.main' : 'action.disabledBackground',
                          mt: { xs: '21px', sm: '23px' },
                          mx: { xs: 1, sm: 2 },
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {showClose && onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
            }}
            aria-label="Close"
          >
            <IoClose size={24} />
          </IconButton>
        )}
      </div>
      {isMobile && (
        <div className=" flex items-center mx-auto w-[90%] my-3 gap-x-2 justify-between">
          <>
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              const isCompleted = activeStep > index;

              return (
                <div className="w-[30%]" key={step}>
                  <LinearProgress
                    variant="determinate"
                    value={isActive || isCompleted ? 100 : 0}
                    sx={{
                      width: '100%',
                      height: 5,
                      borderRadius: '10px',
                      bgcolor: 'action.disabledBackground',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: '10px',
                        bgcolor:
                          isActive || isCompleted ? 'primary.main' : 'action.disabledBackground',
                      },
                    }}
                  />
                </div>
              );
            })}
          </>
        </div>
      )}
    </AppBar>
  );
};

export default WidgetHeader;
